import React, { useEffect, useState, useMemo } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { AccessControlDeviceDetailUI } from './AccessControlDeviceDetailUI'
import { useAccessControlStore } from './AccessControlStore'
import { useGetStationByParkingLot } from '../Lot/utils'
import { DeviceTypeEnum } from '../../data/enums'
import { useControlSwitchMutation } from './control_switch'
import { useUpdateLpAccessConfigMutation } from './update_lp_access_config'
import { ToastTypes, launchToast } from '../../components'
import { ApiError } from '../../utils/useApi'
import { useAuth0 } from '../../utils/auth'
import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { setValueInDiff, deepDelete, ConfigObject } from '../../utils/deviceConfig'
import { updateDeviceDiffConfig } from '../DeviceConfig/device'
import { useSetDeviceForceUpdate } from '../DeviceConfig/utils'

export const AccessControlDeviceDetailContainer = () => {
    const history = useHistory()
    const location = useLocation()
    const { getAuthenticationData } = useAuth0()
    const queryClient = useQueryClient()
    const { devices, sites } = useAccessControlStore()
    const params = new URLSearchParams(location.search)
    const deviceId = params.get('deviceId')

    const currentDevice = useMemo(() => {
        return devices.find((device) => device.device_id === deviceId)
    }, [devices, deviceId])

    const lotSectionId = useMemo(() => {
        const site = sites.find((s) => s.site === currentDevice?.site)
        return site?.pd_lot_id ? `${site.pd_lot_id}_1` : undefined
    }, [sites, currentDevice])

    const lotLive = useMemo(() => {
        const site = sites.find((s) => s.site === currentDevice?.site)
        return site?.live ?? false
    }, [sites, currentDevice])

    const {
        data: stationData,
        loading: stationsLoading,
        refetch: refetchStationData
    } = useGetStationByParkingLot(lotSectionId)

    const stationOptions = useMemo(() => {
        return (
            stationData?.station
                .filter((s) => s.type === DeviceTypeEnum.SCANNER)
                .map((s) => ({
                    label: s.station_name,
                    value: s.station_name
                })) ?? []
        )
    }, [stationData])

    const [lpAccessEnabled, setLpAccessEnabled] = useState(false)
    const [selectedStation, setSelectedStation] = useState('')

    useEffect(() => {
        const mapping = currentDevice?.lp_access_station_mapping
        setLpAccessEnabled(!!mapping)
        setSelectedStation(mapping ?? '')
    }, [currentDevice])

    const { mutate: controlSwitch, isLoading: isSwitchTriggering } = useControlSwitchMutation()
    const { mutate: updateLpAccessConfig, isLoading: isSavingMutation } = useUpdateLpAccessConfigMutation()
    const { setDeviceForceUpdate, loadingForceUpdate } = useSetDeviceForceUpdate()
    const [isApplyingDiffConfig, setIsApplyingDiffConfig] = useState(false)
    const isSaving = isApplyingDiffConfig || isSavingMutation

    const handleBackClick = () => {
        const site = sites.find((s) => s.site === currentDevice?.site)
        history.push(site?.pd_lot_id ? `/lot?lot=${site.pd_lot_id}` : '/lot')
    }

    const LP_ACCESS_CONFIG_PATH = 'vehicle_detection.features.enable_cloud_quick_detection'

    const findStationDevice = (stationName: string) => {
        const station = stationData?.station.find((s) => s.station_name === stationName)
        return station?.device ?? null
    }

    const handleSaveClick = async () => {
        if (!currentDevice?.device || isSaving) return

        const previousStation = currentDevice?.lp_access_station_mapping ?? null
        const newStation = lpAccessEnabled ? selectedStation : null

        const affectedDeviceUuids: string[] = []

        setIsApplyingDiffConfig(true)
        try {
            const { accessToken } = await getAuthenticationData()

            // Disable on old station's device (if old station exists and is changing)
            if (previousStation && previousStation !== newStation) {
                const oldDevice = findStationDevice(previousStation)
                if (oldDevice?.uuid && oldDevice.config_target_timestamp) {
                    const oldDiff = _.cloneDeep((oldDevice.config?.diff ?? {}) as ConfigObject)
                    deepDelete(oldDiff, LP_ACCESS_CONFIG_PATH)
                    await updateDeviceDiffConfig(
                        accessToken,
                        oldDevice.uuid,
                        oldDiff,
                        oldDevice.config_target_timestamp
                    )
                    affectedDeviceUuids.push(oldDevice.uuid)
                }
            }

            // Enable on new station's device (if new station exists and is changing)
            if (newStation && newStation !== previousStation) {
                const newDevice = findStationDevice(newStation)
                if (newDevice?.uuid && newDevice.config_target_timestamp) {
                    const newDiff = _.cloneDeep((newDevice.config?.diff ?? {}) as ConfigObject)
                    setValueInDiff(newDiff, LP_ACCESS_CONFIG_PATH, true)
                    await updateDeviceDiffConfig(
                        accessToken,
                        newDevice.uuid,
                        newDiff,
                        newDevice.config_target_timestamp
                    )
                    affectedDeviceUuids.push(newDevice.uuid)
                }
            }
        } catch (error) {
            launchToast(ToastTypes.Error, 'Error: Could not update device config. Check the console for more details !')
            console.error('Failed to update device diff config:', error)
            setIsApplyingDiffConfig(false)
            return
        }
        setIsApplyingDiffConfig(false)
        //Tech debt : no rollback of device state
        updateLpAccessConfig(
            {
                device: currentDevice.device,
                lp_access_station_mapping: lpAccessEnabled ? selectedStation : null
            },
            {
                onSuccess: () => {
                    refetchStationData()
                    queryClient.invalidateQueries({ queryKey: ['accessControlSitesAndDevices'] })
                    launchToast(ToastTypes.Success, 'Configuration saved successfully!')

                    if (affectedDeviceUuids.length > 0) {
                        const message =
                            affectedDeviceUuids.length === 1
                                ? 'Configuration saved. Force-update the device config now?'
                                : `Configuration saved. Force-update the ${affectedDeviceUuids.length} affected devices now?`
                        if (window.confirm(message)) {
                            affectedDeviceUuids.forEach((device_uuid) => {
                                setDeviceForceUpdate({ device_uuid }).catch(() => {})
                            })
                        }
                    }
                },
                onError: (error) => {
                    launchToast(
                        ToastTypes.Error,
                        'Error: Could not save configuration. Devices might have bad state. Contact AIoT!'
                    )
                    console.error('Failed to save configuration:', error)
                }
            }
        )
    }

    const handleTestDeviceClick = () => {
        if (!currentDevice?.device) return
        controlSwitch(
            { device_name: currentDevice.device },
            {
                onSuccess: () => {
                    launchToast(ToastTypes.Success, 'Switch trigger sent successfully!')
                },
                onError: (error) => {
                    const message =
                        error instanceof ApiError && error.status === 502
                            ? 'Error: Device is unreachable.'
                            : 'Error: Could not send switch trigger. Check the console for more details !'
                    launchToast(ToastTypes.Error, message)
                    console.error('Failed to send switch trigger:', error)
                }
            }
        )
    }

    return (
        <AccessControlDeviceDetailUI
            deviceId={deviceId}
            deviceName={currentDevice?.device || '-'}
            onBackClick={handleBackClick}
            onSaveClick={handleSaveClick}
            isSaving={isSaving || loadingForceUpdate}
            onTestDeviceClick={handleTestDeviceClick}
            isSwitchTriggering={isSwitchTriggering}
            lpAccessEnabled={lpAccessEnabled}
            onLpAccessToggle={setLpAccessEnabled}
            selectedStation={selectedStation}
            onStationChange={setSelectedStation}
            stationOptions={stationOptions}
            stationsLoading={stationsLoading}
            lotLive={lotLive}
        />
    )
}
